import { useEffect, useState } from "react";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Spinner } from "../../components/spinner";
import { findDuplicates, deleteDuplicateCar } from "../../services/duplicates";
import { Snackbar } from "../../components/snackbar";
import { DEFAULT_SNACK } from "../../utils/snacks";

const ITEMS_PER_PAGE = 5;

export const DuplicatesFinder = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedToDelete, setSelectedToDelete] = useState([]);
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      const response = await findDuplicates();
      
      let duplicatesData = [];
      
      if (response.success && Array.isArray(response.data)) {
        duplicatesData = response.data.map(item => ({
          license: item._id,
          count: item.count,
          cars: item.docs || []
        }));
      } else if (Array.isArray(response)) {
        duplicatesData = response;
      }
      
      setDuplicates(duplicatesData);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error loading duplicates:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al cargar duplicados",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedToDelete.length === 0) {
      setSnackbar({
        visible: true,
        severity: "warning",
        message: "Selecciona al menos un vehículo para eliminar",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      return;
    }

    if (
      !window.confirm(
        `¿Estás seguro de eliminar ${selectedToDelete.length} vehículo(s)?`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      
      for (const id of selectedToDelete) {
        await deleteDuplicateCar(id);
      }

      setSnackbar({
        visible: true,
        severity: "success",
        message: `${selectedToDelete.length} vehículo(s) eliminado(s) exitosamente`,
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);

      setSelectedToDelete([]);
      await loadDuplicates();
    } catch (error) {
      console.error("Error deleting cars:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al eliminar vehículos",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (carId, license) => {
    if (!window.confirm(`¿Eliminar el vehículo con patente ${license}?`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteDuplicateCar(carId);
      
      setSnackbar({
        visible: true,
        severity: "success",
        message: "Vehículo eliminado exitosamente",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);

      await loadDuplicates();
    } catch (error) {
      console.error("Error deleting car:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al eliminar vehículo",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleSelect = (carId) => {
    setSelectedToDelete((prev) =>
      prev.includes(carId)
        ? prev.filter((id) => id !== carId)
        : [...prev, carId]
    );
  };

  const totalPages = Math.ceil(duplicates.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentDuplicates = duplicates.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const totalDuplicateRecords = duplicates.reduce(
    (acc, dup) => acc + (dup.cars?.length || 0),
    0
  );

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center p-4 bg-sidebar-bg">
          <Wrapper>
            <Spinner />
          </Wrapper>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4">
        <Wrapper>
          <div className="flex-1 p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-orange-600 flex items-center justify-center">
                        <ContentCopyIcon className="text-white" fontSize="small" />
                      </div>
                      <h1 className="text-3xl font-bold text-white">
                        Patentes Duplicadas
                      </h1>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">
                      Gestiona y elimina registros duplicados • Página {currentPage} de {totalPages}
                    </p>
                  </div>

                  {selectedToDelete.length > 0 && (
                    <button
                      onClick={handleDeleteSelected}
                      disabled={deleting}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white font-medium rounded-lg transition-all flex items-center gap-2"
                    >
                      <DeleteIcon />
                      Eliminar Seleccionados ({selectedToDelete.length})
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <StatCard
                  icon={ContentCopyIcon}
                  title="Patentes Duplicadas"
                  value={duplicates.length}
                  color="orange"
                />
                <StatCard
                  icon={DirectionsCarIcon}
                  title="Total de Registros"
                  value={totalDuplicateRecords}
                  color="blue"
                />
                <StatCard
                  icon={WarningIcon}
                  title="Seleccionados"
                  value={selectedToDelete.length}
                  color="red"
                />
              </div>

              {duplicates.length === 0 ? (
                <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-12 text-center">
                  <CheckCircleIcon
                    className="mx-auto text-green-400 mb-4"
                    sx={{ fontSize: 64 }}
                  />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    ¡No hay duplicados!
                  </h3>
                  <p className="text-gray-400">
                    Todos los registros de patentes son únicos
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    {currentDuplicates.map((duplicate, index) => (
                      <DuplicateGroup
                        key={`${duplicate.license}-${index}`}
                        duplicate={duplicate}
                        selectedToDelete={selectedToDelete}
                        onToggleSelect={handleToggleSelect}
                        onDeleteSingle={handleDeleteSingle}
                        deleting={deleting}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      totalItems={duplicates.length}
                      itemsPerPage={ITEMS_PER_PAGE}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </Wrapper>
      </div>

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />

      {deleting && <Spinner />}
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, color }) => {
  const colorClasses = {
    orange: "bg-orange-500/10 border-orange-500/20 text-orange-400",
    blue: "bg-blue-500/10 border-blue-500/20 text-blue-400",
    red: "bg-red-500/10 border-red-500/20 text-red-400",
  };

  return (
    <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
          <Icon sx={{ fontSize: 28 }} />
        </div>
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-sidebar-bg border-t border-sidebar-accent rounded-xl">
      <div className="text-sm text-gray-400">
        <span className="font-medium text-white">{startItem}</span>-
        <span className="font-medium text-white">{endItem}</span> de{" "}
        <span className="font-medium text-white">{totalItems}</span> patentes duplicadas
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </button>

        <div className="flex gap-1">
          {getPageNumbers().map((page, idx) =>
            page === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-3 py-2 text-gray-500 text-sm"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`min-w-[36px] px-3 py-2 text-sm rounded-lg font-medium transition-all ${
                  currentPage === page
                    ? "bg-orange-600 text-white"
                    : "bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white"
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </button>
      </div>
    </div>
  );
};

const DuplicateGroup = ({
  duplicate,
  selectedToDelete,
  onToggleSelect,
  onDeleteSingle,
  deleting,
}) => {
  const cars = duplicate.cars || [];
  
  return (
    <div className="bg-sidebar-bg rounded-xl border border-orange-500/30 overflow-hidden">
      <div className="px-6 py-4 bg-orange-500/10 border-b border-orange-500/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <WarningIcon className="text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white uppercase">
              {duplicate.license}
            </h3>
            <p className="text-sm text-gray-400">
              {cars.length} registros encontrados
            </p>
          </div>
        </div>
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">
          Duplicado
        </span>
      </div>

      <div className="divide-y divide-sidebar-accent">
        {cars.map((car, idx) => (
          <CarItem
            key={car._id}
            car={car}
            index={idx}
            isSelected={selectedToDelete.includes(car._id)}
            onToggleSelect={onToggleSelect}
            onDelete={onDeleteSingle}
            deleting={deleting}
          />
        ))}
      </div>
    </div>
  );
};

const CarItem = ({ car, index, isSelected, onToggleSelect, onDelete, deleting }) => {
  return (
    <div
      className={`p-6 transition-all ${
        isSelected ? "bg-red-500/5" : "hover:bg-sidebar-accent/30"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="pt-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(car._id)}
            disabled={deleting}
            className="w-5 h-5 rounded border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-900 disabled:opacity-50 cursor-pointer"
          />
        </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <InfoField
            icon={PersonIcon}
            label="Cliente"
            value={car.customer_name}
          />
          <InfoField
            icon={PhoneIcon}
            label="Teléfono"
            value={car.phone || "No disponible"}
          />
          <InfoField
            icon={DirectionsCarIcon}
            label="Vehículo"
            value={`${car.brand || ''} ${car.model || ''}`.trim() || "No especificado"}
          />
          <InfoField
            icon={SpeedIcon}
            label="Kilometraje"
            value={car.kms ? `${car.kms} km` : "No disponible"}
          />
          <InfoField
            icon={CalendarMonthIcon}
            label="Fecha Registro"
            value={car.date || "No disponible"}
          />
          <InfoField
            icon={DirectionsCarIcon}
            label="Servicios"
            value={`${car.services?.length || 0} registrados`}
          />
        </div>

        <button
          onClick={() => onDelete(car._id, car.license)}
          disabled={deleting}
          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
          title="Eliminar este registro"
        >
          <DeleteIcon />
        </button>
      </div>

      <div className="mt-3 pt-3 border-t border-sidebar-accent">
        <p className="text-xs text-gray-500 font-mono">ID: {car._id}</p>
      </div>
    </div>
  );
};

const InfoField = ({ icon: Icon, label, value }) => {
  return (
    <div className="flex items-start gap-3">
      <Icon className="text-gray-500 mt-0.5" sx={{ fontSize: 18 }} />
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-white font-medium mt-0.5 capitalize">
          {value}
        </p>
      </div>
    </div>
  );
};
